import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calculator, DollarSign, Users } from 'lucide-react';

const CPACalculator = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/cpa-calculator`;
  const logoUrl = `${siteUrl}/logo.png`; // Remplacez par l'URL réelle de votre logo
  const [totalCost, setTotalCost] = useState(10000);
  const [acquisitions, setAcquisitions] = useState(100);
  const [cpa, setCpa] = useState(0);

  useEffect(() => {
    calculateCPA();
  }, [totalCost, acquisitions]);

  const calculateCPA = () => {
    const calculatedCpa = acquisitions > 0 ? totalCost / acquisitions : 0;
    setCpa(parseFloat(calculatedCpa.toFixed(2)));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>CPA Calculator: Optimize Your Cost Per Acquisition | Free Marketing Tool</title>
        <meta name="description" content="Calculate and optimize your Cost Per Acquisition (CPA) with our free CPA calculator. Improve your marketing campaigns and ROI. Try it now!" />
        <meta name="keywords" content="CPA calculator, cost per acquisition, customer acquisition, marketing ROI, free marketing tools" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free CPA Calculator: Optimize Your Cost Per Acquisition" />
        <meta property="og:description" content="Calculate and optimize your Cost Per Acquisition (CPA) with our free calculator. Improve your marketing campaigns and ROI. Try it now!" />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free CPA Calculator: Optimize Your Cost Per Acquisition" />
        <meta property="twitter:description" content="Calculate and optimize your Cost Per Acquisition (CPA) with our free calculator. Improve your marketing campaigns and ROI. Try it now!" />
        <meta property="twitter:image" content={logoUrl} />
        
        {/* Last modified date */}
        <meta property="article:modified_time" content="2024-10-23" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "CPA Calculator: Optimize Your Cost Per Acquisition",
            "description": "Calculate and optimize your Cost Per Acquisition (CPA) with our free CPA calculator. Improve your marketing campaigns and ROI.",
            "url": pageUrl,
            "image": logoUrl,
            "publisher": {
              "@type": "Organization",
              "name": "Marketing Calculator Hub",
              "logo": {
                "@type": "ImageObject",
                "url": logoUrl
              }
            }
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        CPA (Cost Per Acquisition) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Cost</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalCost}
                  onChange={(e) => setTotalCost(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Number of Acquisitions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={acquisitions}
                  onChange={(e) => setAcquisitions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${cpa}
            <span className="text-xl text-gray-500 ml-2">per acquisition</span>
          </div>
          <p className="text-gray-600">Cost Per Acquisition (CPA)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation</h2>
        <p className="text-gray-600">
          Your Cost Per Acquisition (CPA) is ${cpa}. This means that, on average, you're spending ${cpa} to acquire each new customer or lead. A lower CPA generally indicates a more efficient marketing campaign, but it's important to consider this metric alongside other factors like customer lifetime value and overall profitability.
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Improve CPA</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize your targeting to reach more qualified prospects</li>
          <li>Improve your landing pages and conversion funnel</li>
          <li>Test different ad creatives and messaging</li>
          <li>Implement retargeting strategies to re-engage interested users</li>
          <li>Focus on channels and campaigns with the lowest CPA</li>
        </ul>
      </div>
    </div>
  );
};

export default CPACalculator;
